import React, { useCallback } from 'react';

import { Text } from 'components';
import useImpressionTracking from 'hooks/useImpressionTracking';
import { SearchShelfCardSection, TSearchShelfCard } from 'types';
import {
  claimTrackingSlot,
  getShelfCardClickTrackingKey,
  getShelfCardImpressionTrackingKey,
  trackSearchShelfCardClick,
  trackSearchShelfCardImpression,
} from 'utils';

export type ShelfCardType = 'product' | 'job';

type ShelfCardProps = {
  card: TSearchShelfCard;
  type: ShelfCardType;
  userId?: number;
  isActive: boolean;
  isTrackingReady: boolean;
  onSelect: (card: TSearchShelfCard) => void;
};

const getShelfCardSection = (type: ShelfCardType): SearchShelfCardSection =>
  type === 'job' ? 'JOB' : 'PRODUCT';

function ShelfCard({
  card,
  type,
  userId,
  isActive,
  isTrackingReady,
  onSelect,
}: ShelfCardProps) {
  const section = getShelfCardSection(type);
  const trackingKey = getShelfCardImpressionTrackingKey(
    section,
    card.id,
    userId,
  );

  const handleImpression = useCallback(() => {
    trackSearchShelfCardImpression({
      cardId: card.id,
      cardTitle: card.title,
      section,
      userId,
    });
  }, [card.id, card.title, section, userId]);

  const cardRef = useImpressionTracking<HTMLButtonElement>({
    trackingKey,
    isActive,
    onImpression: handleImpression,
  });

  return (
    <button
      ref={cardRef}
      type="button"
      aria-label={card.title}
      className="block w-full overflow-hidden bg-white text-left"
      onClick={() => {
        if (
          isTrackingReady &&
          claimTrackingSlot(
            getShelfCardClickTrackingKey(section, card.id, userId),
          )
        ) {
          trackSearchShelfCardClick({
            cardId: card.id,
            cardTitle: card.title,
            section,
            userId,
          });
        }
        onSelect(card);
      }}
    >
      <img
        src={card.imageUrl}
        alt=""
        className="w-full aspect-[2/3] object-cover rounded-lg bg-neutral-100"
      />
      <Text bold fontSize={12} className="mt-1 line-clamp-2">
        {card.title}
      </Text>
    </button>
  );
}

export default ShelfCard;
